
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserPlus, Search, X } from 'lucide-react';
import { useClientsInfinite } from '@/hooks/clients/useClientsList';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import ClientForm from '@/components/clients/ClientForm';
import ClientsList from '@/components/clients/ClientsList';
import ClientFilters from '@/components/clients/ClientFilters';
import { Client } from '@/models/Client';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { useWorkoutTypes } from '@/hooks/useWorkoutTypes';
import { useToast } from '@/hooks/use-toast';
import { 
  ClientsPaginationState,
  CLIENTS_PAGINATION_CONFIG,
  ClientsSortField,
  ClientsSortDirection
} from '@/constants/clientsPagination';
import { ClientsInfiniteResult } from '@/hooks/clients/useClientsList';
import { 
  getClientsListState, 
  saveClientsListState, 
  ClientsListState 
} from '@/utils/localStorage';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';



const Clients = () => {
  const { user, loading, currentOrganization } = useAuth();
  const { t } = useTranslation();
  const { canManageClients } = useRolePermissions();
  const { workoutTypes = [] } = useWorkoutTypes();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const hasProcessedInitialUrlParams = useRef(false);

  // Initialize state from localStorage (organization-specific)
  const [filters, setFilters] = useState<Omit<ClientsPaginationState, 'page' | 'pageSize'>>(() => {
    const savedState = getClientsListState(currentOrganization?.id);
    setSearchInput(savedState.searchTerm || '');
    setDebouncedSearchTerm(savedState.searchTerm || '');
    return {
      sortField: savedState.sortField,
      sortDirection: savedState.sortDirection,
      searchTerm: savedState.searchTerm,
      selectedWorkoutType: savedState.selectedWorkoutType,
      showSoonExpire: savedState.showSoonExpire,
      showUnpaidOnly: savedState.showUnpaidOnly,
      showNoSubscriptionsOnly: savedState.showNoSubscriptionsOnly ?? false, // Use saved state or default to all clients
    };
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchInput);
    }, CLIENTS_PAGINATION_CONFIG.DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Update filters when debounced search term changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, searchTerm: debouncedSearchTerm }));
  }, [debouncedSearchTerm]);

  // Save state to localStorage whenever filters change
  useEffect(() => {
    const state: ClientsListState = {
      sortField: filters.sortField,
      sortDirection: filters.sortDirection,
      searchTerm: filters.searchTerm,
      selectedWorkoutType: filters.selectedWorkoutType,
      showSoonExpire: filters.showSoonExpire,
      showUnpaidOnly: filters.showUnpaidOnly,
      showNoSubscriptionsOnly: filters.showNoSubscriptionsOnly,
    };
    saveClientsListState(state, currentOrganization?.id);
  }, [filters, currentOrganization?.id]);

  const { 
    data, 
    isLoading, 
    error, 
    fetchNextPage, 
    hasNextPage, 
    isFetching, 
    isFetchingNextPage 
  } = useClientsInfinite(filters);

  // Flatten all pages of data
  const allClients = data?.pages.flatMap((page: ClientsInfiniteResult) => page.clients) || [];
  const totalItems = data?.pages[0]?.totalItems || 0;

  // Infinite scroll hook
  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: hasNextPage || false,
    isFetching: isFetching || isFetchingNextPage,
    fetchNextPage,
  });

  // Enhanced scroll restoration hook
  const scrollRestoration = useScrollRestoration({
    items: allClients,
    enabled: true,
    debounceMs: 100,
    restoreDelay: 300,
    organizationId: currentOrganization?.id
  });

  // Handle URL parameters only on initial mount - don't override saved filters when navigating back
  useEffect(() => {
    if (hasProcessedInitialUrlParams.current) return;
    
    const paymentStatus = searchParams.get('payment_status');
    const sessions = searchParams.get('sessions');
    
    // Only apply URL params on first load if they're present
    if (paymentStatus === 'unpaid' || sessions === 'low') {
      setFilters(prev => ({
        ...prev,
        showUnpaidOnly: paymentStatus === 'unpaid',
        showSoonExpire: sessions === 'low',
      }));
    }
    
    hasProcessedInitialUrlParams.current = true;
  }, []); // Empty dependency array - only run on mount

  // Filter handlers
  const handleSortChange = (sortField: ClientsSortField, sortDirection: ClientsSortDirection) => {
    setFilters(prev => ({ ...prev, sortField, sortDirection }));
  };

  const handleSearchChange = (searchTerm: string) => {
    setSearchInput(searchTerm);
  };

  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleResetFilters = () => {
    setFilters({
      sortField: CLIENTS_PAGINATION_CONFIG.DEFAULT_SORT_FIELD,
      sortDirection: CLIENTS_PAGINATION_CONFIG.DEFAULT_SORT_DIRECTION,
      searchTerm: '',
      selectedWorkoutType: null,
      showSoonExpire: false,
      showUnpaidOnly: false,
      showNoSubscriptionsOnly: false, // Show all clients by default
    });
    setSearchInput('');
    setDebouncedSearchTerm('');
  };


  // Handle authentication loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const handleEditClient = (client: Client) => {
    if (canManageClients) {
      setEditingClient(client);
    }
  };

  const handleCloseEditModal = () => {
    setEditingClient(null);
  };

  const handleAddClientClick = () => {
    if (!workoutTypes.length) {
      toast({
        title: t('clients.errors.noWorkoutTypes.title'),
        description: t('clients.errors.noWorkoutTypes.description'),
        variant: "destructive",
      });
      return;
    }
    setIsAddClientOpen(true);
  };

  if (error) {
    return (
      <MainLayout>
        <div className="py-4 md:py-6">
          <div className="text-center text-destructive">
            {t('common.error')} loading clients. Please try again.
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="py-4 md:py-6 space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4">
          <h1 className="text-2xl md:text-3xl font-bold">
            {t('clients.title')}
          </h1>
          {canManageClients && (
            <Button
              onClick={handleAddClientClick}
              className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm"
              size="sm"
            >
              <UserPlus className="h-4 w-4 md:h-5 md:w-5" />
              <span>{t('clients.add')}</span>
            </Button>
          )}
        </div>

        <div className="relative w-full">
          <Search className="absolute left-2.5 md:left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-3 w-3 md:h-4 md:w-4" />
          <Input
            placeholder={t('clients.search')}
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-8 md:pl-10 pr-8 md:pr-10 text-xs md:text-sm h-8 md:h-10 w-full"
          />
          {searchInput && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchInput('');
                handleSearchChange('');
              }}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3 md:h-4 md:w-4" />
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-4 items-center justify-between">
          <ClientFilters
            selectedWorkoutType={filters.selectedWorkoutType}
            onWorkoutTypeChange={(workoutType) => handleFilterChange({ selectedWorkoutType: workoutType })}
            showSoonExpire={filters.showSoonExpire}
            onSoonExpireChange={(showSoonExpire) => handleFilterChange({ showSoonExpire })}
            showUnpaidOnly={filters.showUnpaidOnly}
            onUnpaidOnlyChange={(showUnpaidOnly) => handleFilterChange({ showUnpaidOnly })}
            sortField={filters.sortField}
            sortDirection={filters.sortDirection}
            onSortChange={handleSortChange}
            onResetFilters={handleResetFilters}
            isLoading={isLoading}
          />
          
          {totalItems > 0 && (
            <div className="text-sm text-muted-foreground whitespace-nowrap">
              {t('common.pagination.showing')} {allClients.length}/{totalItems} {t('common.pagination.results')}
            </div>
          )}
        </div>

        {!canManageClients && (
          <div className="mb-4 p-3 md:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-xs md:text-sm">
              You can view clients but don't have permission to manage them. Only admins and managers can create, edit, or delete clients.
            </p>
          </div>
        )}

        <ClientsList
          clients={allClients}
          onEditClient={handleEditClient}
          isLoading={isLoading}
          canManage={canManageClients}
          onClientClick={(client) => {
            scrollRestoration.saveClientNavigation(client.id);
          }}
        />


        {/* Infinite scroll trigger */}
        {(hasNextPage || isFetchingNextPage) && (
          <div ref={loadMoreRef} className="flex justify-center py-4">
            {isFetchingNextPage ? (
              <div className="text-sm text-muted-foreground">
                {t('common.loading')}...
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                {t('common.loading')}...
              </div>
            )}
          </div>
        )}
      </div>

      {canManageClients && (
        <ClientForm 
          open={isAddClientOpen} 
          onOpenChange={setIsAddClientOpen} 
          organizationId={currentOrganization?.id}
        />
      )}

      {editingClient && canManageClients && (
        <ClientForm
          open={!!editingClient}
          onOpenChange={handleCloseEditModal}
          initialData={editingClient}
          organizationId={currentOrganization?.id}
        />
      )}
    </MainLayout>
  );
};

export default Clients;
